import { Injectable, signal, effect } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError, map, switchMap, of } from 'rxjs';
import {
  Cliente,
  CreateClienteRequest,
  CreateClienteResponse,
  BusinessCustomersResponse,
  UpdateClienteRequest,
  UpdateStatusRequest
} from '../models/sala.model';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class CustomerService {
  private readonly API_URL = `${environment.apiUrl}/auth/business`;

  // Señal reactiva para los clientes
  public readonly clientes = signal<Cliente[]>([]);

  private lastBusinessId: number | null | undefined = undefined;

  constructor(private http: HttpClient, private auth: AuthService) {
    // Multi-tenant: evita que los clientes de un negocio queden visibles al
    // cambiar de cuenta en la misma pestaña (ver SalaService.reset).
    effect(() => {
      const businessId = this.auth.currentUser()?.id ?? null;
      if (this.lastBusinessId !== undefined && businessId !== this.lastBusinessId) {
        this.reset();
      }
      this.lastBusinessId = businessId;
    });
  }

  /** Limpia el estado en memoria al cambiar de sesión/negocio. */
  reset(): void {
    this.clientes.set([]);
  }

  /**
   * Crear un nuevo cliente
   */
  crearCliente(cliente: CreateClienteRequest): Observable<CreateClienteResponse> {
    return this.http.post<CreateClienteResponse>(`${this.API_URL}/customers`, cliente).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Listar todos los clientes de un negocio
   */
  getClientesPorNegocio(businessId: number): Observable<Cliente[]> {
    return this.getClientesPage(businessId, 0, []).pipe(
      catchError(this.handleError)
    );
  }

  getClientesPaginados(
    businessId: number,
    page = 1,
    limit = 25,
    search = ''
  ): Observable<BusinessCustomersResponse> {
    const safePage = Math.max(1, Math.trunc(page));
    const safeLimit = Math.min(100, Math.max(1, Math.trunc(limit)));
    return this.http.get<BusinessCustomersResponse>(
      `${this.API_URL}/${businessId}/customers`,
      {
        params: {
          limit: safeLimit,
          offset: (safePage - 1) * safeLimit,
          ...(search.trim() ? { search: search.trim() } : {})
        }
      }
    ).pipe(catchError(this.handleError));
  }

  private getClientesPage(businessId: number, offset: number, accumulated: Cliente[]): Observable<Cliente[]> {
    const limit = 100;
    return this.http.get<BusinessCustomersResponse>(
      `${this.API_URL}/${businessId}/customers`,
      { params: { limit, offset } }
    ).pipe(
      switchMap(response => {
        const customers = [...accumulated, ...(response.customers || [])];
        return customers.length < response.total_customers
          ? this.getClientesPage(businessId, offset + limit, customers)
          : of(customers);
      })
    );
  }

  /**
   * Obtener un cliente específico
   */
  getClientePorId(businessId: number, customerId: number): Observable<Cliente> {
    return this.http.get<{ customer: Cliente }>(`${this.API_URL}/${businessId}/customers/${customerId}`).pipe(
      map(response => response.customer),
      catchError(this.handleError)
    );
  }

  /**
   * Editar un cliente
   */
  editarCliente(businessId: number, customerId: number, cliente: UpdateClienteRequest): Observable<Cliente> {
    return this.http.put<{ customer: Cliente }>(`${this.API_URL}/${businessId}/customers/${customerId}`, cliente).pipe(
      map(response => response.customer),
      catchError(this.handleError)
    );
  }

  /**
   * Activar o desactivar un cliente
   */
  actualizarEstadoCliente(businessId: number, customerId: number, isActive: boolean): Observable<Cliente> {
    const statusRequest: UpdateStatusRequest = { is_active: isActive };
    return this.http.patch<{ customer: Cliente }>(`${this.API_URL}/${businessId}/customers/${customerId}/status`, statusRequest).pipe(
      map(response => response.customer),
      catchError(this.handleError)
    );
  }

  /**
   * Enviar al cliente un enlace seguro para restablecer su contraseña.
   */
  enviarRecuperacionContrasena(businessId: number, customerId: number): Observable<{ msg: string }> {
    return this.http.post<{ msg: string }>(
      `${this.API_URL}/${businessId}/customers/${customerId}/password-recovery`,
      { frontend_base_url: environment.clientAppUrl }
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Eliminar (desvincular) un cliente del negocio
   */
  eliminarCliente(businessId: number, customerId: number, reason: string): Observable<void> {
    return this.http.delete<void>(`${this.API_URL}/${businessId}/customers/${customerId}`, { body: { reason } }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Cargar clientes en la señal reactiva
   */
  loadClientes(businessId: number): void {
    this.getClientesPorNegocio(businessId).subscribe({
      next: (clientes) => {
        this.clientes.set(clientes);
      },
      error: (error) => {
        console.error('Error al cargar clientes:', error);
      }
    });
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    console.error('Error HTTP:', error.error?.msg || error.error?.message || error.message);
    return throwError(() => error);
  }
}
